import React from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { UserRole } from 'types/userRoles';
import { Button, Chip } from 'common/components';
import { Table, TableColumn } from 'common/components/Table';
import { AddUserModal } from '../AddUserModal';
import { UsersSectionProps, MappedUser } from './UsersSection.types';
import styles from './UsersSection.module.scss';

const UsersSection = ({
    users,
    isTenantLoading,
    onEditUser,
    onDeleteUser,
    isAddUserModalOpen,
    onOpenAddUserModal,
    onCloseAddUserModal,
    onAddUserSuccess,
    onAddUserError,
    hasTenantAccess,
    tenants,
    selectedTenant,
    selectedUser,
    currentUserTenantId,
}: UsersSectionProps) => {
    const getRoleLabel = (roles: string[]): string => {
        if (roles.includes(UserRole.SUPER_ADMIN)) {
            return 'Super Admin';
        }
        if (roles.includes(UserRole.ADMIN)) {
            return 'Admin';
        }
        if (roles.includes(UserRole.MANAGER)) {
            return 'Manager';
        }
        return 'Agent';
    };

    const columns: TableColumn<MappedUser>[] = [
        {
            key: 'username',
            title: 'Username',
            render: (_, record) => `${record.firstName} ${record.lastName}`,
        },
        {
            key: 'email',
            title: 'Email',
            dataIndex: 'email',
        },
        {
            key: 'role',
            title: 'Role',
            render: (_, record) => getRoleLabel(record.roles),
        },
        {
            key: 'status',
            title: 'Status',
            render: () => (
                <Chip variant='filled' color='success' size='sm'>
                    Active
                </Chip>
            ),
        },
        {
            key: 'actions',
            title: 'Actions',
            render: (_, record) => (
                <div className={styles.actionsCell}>
                    <Button
                        variant='outline-secondary'
                        size='sm'
                        icon={Pencil}
                        iconOnly
                        onClick={() => onEditUser(record)}
                        title='Edit user'
                    />
                    <Button
                        variant='outline-secondary'
                        size='sm'
                        icon={Trash2}
                        iconOnly
                        onClick={() => onDeleteUser(record)}
                        title='Delete user'
                    />
                </div>
            ),
        },
    ];

    return (
        <>
            <div className={styles.usersHeader}>
                <h2>Users {selectedTenant ? `for ${selectedTenant.name}` : ''}</h2>
                <Button variant='primary' size='sm' onClick={onOpenAddUserModal}>
                    Add User
                </Button>
            </div>
            <Table
                data={users}
                columns={columns}
                isLoading={isTenantLoading}
                emptyText='No users found'
                rowKey='id'
                hoverable
                variant='secondary'
                className={styles.usersTableWrapper}
            />

            {isAddUserModalOpen && (
                <AddUserModal
                    isOpen={isAddUserModalOpen}
                    onClose={onCloseAddUserModal}
                    onSuccess={onAddUserSuccess}
                    onError={onAddUserError}
                    hasTenantAccess={hasTenantAccess}
                    tenants={tenants}
                    selectedTenant={selectedTenant}
                    selectedUser={selectedUser}
                    currentUserTenantId={currentUserTenantId}
                />
            )}
        </>
    );
};

export default UsersSection;
